import type { EventDto, MetricLatestResponse } from '@vision360/contracts';
import { lazy } from 'react';
import { z } from 'zod';
import { toDataState, useEvents, useMetricLatest } from '../_sdk/hooks';
import { decimalsSchema, unitSchema } from '../_sdk/schema';
import { defineWidget, type WidgetDataState } from '../_sdk/types';

/**
 * Sortable, filterable, paged table (PRD VS-042): events, or the newest value
 * of every series of one metric. Sorting, filtering and paging are
 * client-side over at most 200 rows.
 */

export const EVENT_COLUMNS = ['time', 'severity', 'kind', 'title', 'source'] as const;
export const PAGE_SIZES = [10, 25, 50] as const;

const TAG_PREFIX = 'tag:';
export const tagColumn = (key: string) => `${TAG_PREFIX}${key}`;
export const tagOfColumn = (column: string): string | null =>
  column.startsWith(TAG_PREFIX) ? column.slice(TAG_PREFIX.length) : null;

/** Metric columns: every tag key present (sorted), then value and time. */
export function metricColumns(tagSets: readonly Record<string, string>[]): string[] {
  const keys = [...new Set(tagSets.flatMap((tags) => Object.keys(tags)))].sort();
  return [...keys.map(tagColumn), 'value', 'time'];
}

/** The chosen columns that exist, in table order; none chosen (or none left) = all. */
export function visibleColumns(available: readonly string[], chosen: readonly string[]): string[] {
  const picked = available.filter((c) => chosen.includes(c));
  return picked.length > 0 ? picked : [...available];
}

export const PropsSchema = z
  .object({
    label: z.string().max(80).optional(),
    mode: z.enum(['events', 'metrics']).default('events'),
    /** Events: empty = every source. Metrics: required. */
    sourceId: z.uuid().optional(),
    kindPrefix: z.string().max(200).optional(),
    minSeverity: z.number().int().min(0).max(4).default(0),
    limit: z.number().int().min(10).max(200).default(100),
    metricName: z.string().min(1).max(200).optional(),
    unit: unitSchema,
    decimals: decimalsSchema,
    /** Column ids (`time`, `tag:host`, …); empty = every column. */
    columns: z.array(z.string().min(1).max(80)).max(30).default([]),
    pageSize: z.union([z.literal(10), z.literal(25), z.literal(50)]).default(10),
  })
  .refine((p) => p.mode === 'events' || Boolean(p.sourceId && p.metricName));

export type TableProps = z.output<typeof PropsSchema>;
export type TableData =
  | { mode: 'events'; events: EventDto[] }
  | { mode: 'metrics'; items: MetricLatestResponse['items'] };

const newest = (timestamps: string[]) => {
  const ms = Math.max(0, ...timestamps.map((ts) => Date.parse(ts)));
  return ms > 0 ? new Date(ms) : undefined;
};

function useEventRows(p: TableProps): WidgetDataState<TableData> {
  return toDataState(
    useEvents({
      sourceId: p.sourceId || undefined,
      kindPrefix: p.kindPrefix || undefined,
      minSeverity: p.minSeverity || undefined,
      limit: p.limit,
    }),
    {
      select: (r): TableData => ({ mode: 'events', events: r.events }),
      isEmpty: (d) => d.mode === 'events' && d.events.length === 0,
      lastUpdated: (d) => (d.mode === 'events' ? newest(d.events.map((e) => e.ts)) : undefined),
    },
  );
}

function useMetricRows(p: TableProps): WidgetDataState<TableData> {
  return toDataState(useMetricLatest(p.sourceId ?? '', p.metricName ?? '', {}), {
    select: (r): TableData => ({ mode: 'metrics', items: r.items }),
    isEmpty: (d) => d.mode === 'metrics' && d.items.length === 0,
    lastUpdated: (d) => (d.mode === 'metrics' ? newest(d.items.map((i) => i.ts)) : undefined),
  });
}

export default defineWidget<TableProps, TableData>({
  type: 'table',
  category: 'composite',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 4, h: 3 },
  propsSchema: PropsSchema,
  defaults: {
    mode: 'events',
    minSeverity: 0,
    limit: 100,
    decimals: 0,
    columns: [],
    pageSize: 10,
  },
  sourceIds: (p) => (p.sourceId ? [p.sourceId] : []),
  // One hook per mode. The frame remounts an instance whenever its props change
  // (its error boundary is keyed by them), so an instance never switches hooks.
  useData: (p) => (p.mode === 'events' ? useEventRows : useMetricRows)(p),
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
